<script lang="ts">
  import { defineComponent } from "vue";
  import SectionManualBillingDocument from '@/generated/sections/SectionManualBillingDocument.vue';
  import ManualBillingDocument, {
    ManualBillingDocumentKinds,
    ManualBillingDocumentStatuts,
  } from '@/generated/classes/ManualBillingDocument';
  import Button from '@/framework/base/classes/Button';
  import Dialog from '@/generated/classes/Dialog';
  import { Alerts } from '@/framework/base/helpers/Alerts';
  import { storeManualBillingDocument } from '@/generated/stores';

  export default defineComponent({
    extends: SectionManualBillingDocument,
    components: {},
    data() {
      return {
        autoClose: false,
        actionLoading: false,
        useVariableVat: false,
      };
    },
    created(): void {
      ManualBillingDocument.vatPolicy().then((enabled) => {
        this.useVariableVat = enabled;
        this.applyVatPolicy();
      });
    },
    watch: {
      saveLoading(value: boolean, previousValue: boolean): void {
        if (previousValue &&
            !value &&
            !this.manualBillingDocumentSaving &&
            this.refManualBillingDocument.compareManualBillingDocument(this.cloneManualBillingDocument)) {
          this.updated = false;
          this.status.updated = false;
          this.$emit('status', this.status);
        }
      },
    },
    computed: {
      saveButton(): boolean {
        return (this.showButton == undefined || this.showButton == true) &&
          this.editManualBillingDocument.canUpdate;
      },
      extraButtons(): Button[] {
        const buttons: Button[] = [];
        const document = this.editManualBillingDocument;

        if (document.id > 0 &&
            (document.statut == ManualBillingDocumentStatuts.DRAFT ||
             document.statut == ManualBillingDocumentStatuts.PROPOSED)) {
          const issue = new Button();
          if (document.kind == ManualBillingDocumentKinds.INVOICE && document.inCart)
            issue.label = 'Ajouter au panier';
          else if (document.kind == ManualBillingDocumentKinds.CREDIT_NOTE)
            issue.label = 'Émettre l’avoir';
          else if (document.kind == ManualBillingDocumentKinds.CREDIT)
            issue.label = 'Émettre la note de crédit';
          else if (document.kind == ManualBillingDocumentKinds.DEBIT)
            issue.label = 'Émettre la note de débit';
          else
            issue.label = 'Émettre la facture';
          issue.type = 'button';
          issue.outlined = false;
          issue.loading = this.actionLoading;
          issue.disabled = this.actionLoading || this.updated || this.error;
          issue.callback = () => this.issueDocument();
          buttons.push(issue);
        }

        if (document.id > 0 &&
            (document.statut == ManualBillingDocumentStatuts.ISSUED ||
             document.statut == ManualBillingDocumentStatuts.WAITING_PAYMENT)) {
          const cancel = new Button();
          cancel.label = document.statut == ManualBillingDocumentStatuts.WAITING_PAYMENT
            ? 'Retirer du panier'
            : 'Annuler le document';
          cancel.type = 'button';
          cancel.outlined = true;
          cancel.loading = this.actionLoading;
          cancel.disabled = this.actionLoading;
          cancel.callback = () => this.confirmCancellation();
          buttons.push(cancel);
        }

        return buttons;
      },
    },
    methods: {
      isDisplayed(value: string): boolean {
        const adjustment = this.editManualBillingDocument.kind == ManualBillingDocumentKinds.CREDIT ||
          this.editManualBillingDocument.kind == ManualBillingDocumentKinds.DEBIT;
        if (adjustment && ['AMOUNT_HT', 'VAT_RATE', 'AMOUNT_TVA'].includes(value))
          return false;

        if (this.displayMap.size == 0) {
          const requiredAttributs = this.getRequiredAttributs();
          this.displayMap.set('ETABLISSEMENT_ID', true);
          this.displayMap.set('KIND', true);
          this.displayMap.set('IN_CART', true);
          this.displayMap.set('LEDGER_CODE', true);
          this.displayMap.set('EFFECTIVE_AT', true);
          this.displayMap.set('DOCUMENT_REFERENCE',
            this.requiredMissing(requiredAttributs, 'DOCUMENT_REFERENCE') || !!this.fiche);
          this.displayMap.set('REFERENCE', true);
          this.displayMap.set('LABEL', true);
          this.displayMap.set('AMOUNT_HT', true);
          this.displayMap.set('VAT_RATE', true);
          this.displayMap.set('AMOUNT_TVA', true);
          this.displayMap.set('AMOUNT_TTC', true);
          this.displayMap.set('PAYMENT_METHOD',
            this.requiredMissing(requiredAttributs, 'PAYMENT_METHOD') || !!this.fiche);
        }
        return this.displayMap.get(value) ?? true;
      },
      readOnly(value: string): boolean {
        if (!this.editManualBillingDocument.canUpdate)
          return true;
        if (value == 'VAT_RATE') {
          return !this.useVariableVat ||
            this.editManualBillingDocument.kind == ManualBillingDocumentKinds.CREDIT ||
            this.editManualBillingDocument.kind == ManualBillingDocumentKinds.DEBIT;
        }
        if (this.readOnlyMap.size == 0) {
          this.readOnlyMap.set('ETABLISSEMENT_ID', false);
          this.readOnlyMap.set('KIND', false);
          this.readOnlyMap.set('IN_CART', false);
          this.readOnlyMap.set('LEDGER_CODE', false);
          this.readOnlyMap.set('EFFECTIVE_AT', false);
          this.readOnlyMap.set('DOCUMENT_REFERENCE', true);
          this.readOnlyMap.set('REFERENCE', false);
          this.readOnlyMap.set('LABEL', false);
          this.readOnlyMap.set('AMOUNT_HT', false);
          this.readOnlyMap.set('VAT_RATE', false);
          this.readOnlyMap.set('AMOUNT_TVA', false);
          this.readOnlyMap.set('AMOUNT_TTC', false);
          this.readOnlyMap.set('PAYMENT_METHOD', false);
        }
        return this.readOnlyMap.get(value) ?? false;
      },
      applyVatPolicy(): void {
        const adjustment = this.editManualBillingDocument.kind == ManualBillingDocumentKinds.CREDIT ||
          this.editManualBillingDocument.kind == ManualBillingDocumentKinds.DEBIT;
        if (adjustment) {
          this.editManualBillingDocument.vatRate = 0;
          this.editManualBillingDocument.amountTva = 0;
          if (this.editManualBillingDocument.amountTtc == 0)
            this.editManualBillingDocument.amountTtc = this.editManualBillingDocument.amountHt;
          this.editManualBillingDocument.amountHt = this.editManualBillingDocument.amountTtc;
          this.strVatRate = '0';
          this.strAmountTva = '0';
          this.strAmountHt = this.editManualBillingDocument.amountHt.toString();
          this.strAmountTtc = this.editManualBillingDocument.amountTtc.toString();
          return;
        } else if (!this.useVariableVat)
          this.editManualBillingDocument.vatRate = 20;
        else
          return;

        this.strVatRate = this.editManualBillingDocument.vatRate.toString();
        this.updateVatAmounts();
      },
      updateVatAmounts(): void {
        const amountHt = this.editManualBillingDocument.amountHt;
        const vatRate = this.editManualBillingDocument.vatRate;
        const amountTva = Math.round((amountHt * vatRate / 100 + Number.EPSILON) * 100) / 100;
        const amountTtc = Math.round((amountHt + amountTva + Number.EPSILON) * 100) / 100;
        this.editManualBillingDocument.amountTva = amountTva;
        this.editManualBillingDocument.amountTtc = amountTtc;
        this.strAmountTva = amountTva.toString();
        this.strAmountTtc = amountTtc.toString();
      },
      fieldUpdated(field: string, _value: unknown): void {
        if (field == 'KIND') {
          this.applyVatPolicy();
          field = 'VAT_RATE';
        }
        const adjustment = this.editManualBillingDocument.kind == ManualBillingDocumentKinds.CREDIT ||
          this.editManualBillingDocument.kind == ManualBillingDocumentKinds.DEBIT;
        if (adjustment && field == 'AMOUNT_TTC') {
          this.editManualBillingDocument.amountHt = this.editManualBillingDocument.amountTtc;
          this.editManualBillingDocument.amountTva = 0;
          this.strAmountHt = this.editManualBillingDocument.amountHt.toString();
          this.strAmountTva = '0';
          return;
        }
        if (field != 'AMOUNT_HT' && field != 'VAT_RATE')
          return;
        this.updateVatAmounts();
      },
      issueDocument(): Promise<void> {
        this.actionLoading = true;
        this.saveFailed = false;
        return ManualBillingDocument.issue(this.editManualBillingDocument)
          .then((document) => {
            this.applyServerDocument(document);
          })
          .catch(() => {
            this.saveFailed = true;
            this.showActionError("Le traitement de la facture ou de l’avoir a échoué.");
          })
          .finally(() => {
            this.actionLoading = false;
          });
      },
      confirmCancellation(): Promise<void> {
        const waitingPayment = this.editManualBillingDocument.statut ==
          ManualBillingDocumentStatuts.WAITING_PAYMENT;
        return Alerts.alertDelete(
          waitingPayment
            ? 'Confirmer le retrait de cette facture du panier ?'
            : 'Confirmer l’annulation ? Un document comptable de sens inverse sera émis.'
        ).then(() => this.cancelDocument());
      },
      cancelDocument(): Promise<void> {
        this.actionLoading = true;
        this.saveFailed = false;
        return ManualBillingDocument.cancel(this.editManualBillingDocument)
          .then((document) => {
            this.applyServerDocument(document);
          })
          .catch(() => {
            this.saveFailed = true;
            this.showActionError("L’annulation du document a échoué.");
          })
          .finally(() => {
            this.actionLoading = false;
          });
      },
      applyServerDocument(document: ManualBillingDocument): void {
        storeManualBillingDocument.mManualBillingDocumentUpdated(document);
        const storedDocument = storeManualBillingDocument.getManualBillingDocument(document.id);
        this.createRef(storedDocument);
        this.$emit('saved', storedDocument);
      },
      showActionError(label: string): void {
        const dialog = new Dialog();
        dialog.label = label;
        Alerts.message(dialog);
      },
    },
  })
</script>
